import {
  Bike,
  Brain,
  Bus,
  Code,
  Github,
  Globe,
  Linkedin,
  Lock,
  Mail,
  MessageCircle,
  Shield,
  Train,
  Zap,
} from "lucide-react";
import React, { useEffect } from "react";
import { Card, CardContent } from "./components/ui/card";
import { trackLinkClick, trackPageView } from "./utils/analytics";

const LandingPage = () => {
  // Track page view when component mounts
  useEffect(() => {
    trackPageView("home");
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Hero Section with enhanced styling */}
      <header className="py-24 px-4 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-100/30 to-purple-100/30 animate-gradient-x"></div>
        <div className="max-w-4xl mx-auto relative">
          <h1 className="text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-6">
            Mark Ballew
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 font-light mb-4">
            Engineer by trade, advocate by passion, explorer at heart
          </p>
          <p className="text-lg text-gray-600 italic">
            "I fight for the users" – Building safe AI systems for humanity
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 pb-20 space-y-12">
        {/* About Section */}
        <section className="transform hover:-translate-y-1 transition-transform duration-300">
          <Card className="overflow-hidden border-none shadow-lg bg-white/80 backdrop-blur-sm">
            <CardContent className="p-8">
              <h2 className="text-2xl font-semibold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                About Me
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    Hi, I'm Mark Ballew. I live in the Bay Area with my
                    girlfriend, Lara, and our two cats, Ziggy and Zuzu. Fun
                    fact: Ziggy and Zuzu are brothers, and Zuzu is a polydactyl
                    cat with extra toes!
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    I'm a DevOps engineer passionate about building reliable,
                    secure infrastructure that serves people. In my spare time,
                    I create everything from props to T-shirts using 3D
                    printing, especially for events like races, birthdays, and
                    holidays.
                  </p>
                  <div className="flex flex-col space-y-3 mt-6">
                    <div className="flex items-center space-x-3 text-gray-600 bg-blue-50 p-3 rounded-lg">
                      <Globe className="w-5 h-5 text-blue-500" />
                      <span>Based in the Bay Area</span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-600 bg-purple-50 p-3 rounded-lg">
                      <MessageCircle className="w-5 h-5 text-purple-500" />
                      <span>Learning Portuguese & Spanish</span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-600 bg-indigo-50 p-3 rounded-lg">
                      <span className="font-medium">
                        🖨️ 3D Printing Enthusiast
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <img
                    src="/sfcat.jpg"
                    alt="San Francisco themed illustration"
                    className="rounded-xl shadow-lg w-full hover:scale-105 transition-transform duration-300"
                  />
                  <p className="text-sm text-gray-500 mt-3 italic text-center">
                    SF City scape
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Work Section - AI Safety Focus */}
        <section className="transform hover:-translate-y-1 transition-transform duration-300">
          <Card className="border-none shadow-lg bg-white/80 backdrop-blur-sm">
            <CardContent className="p-8">
              <h2 className="text-2xl font-semibold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                AI Safety & Infrastructure
              </h2>
              <div className="space-y-8">
                {/* Main Work Description */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-100 rounded-xl p-8">
                  <h3 className="text-xl font-medium mb-6 text-blue-700 flex items-center gap-2">
                    Building Safe AI Systems{" "}
                    <Brain className="w-5 h-5" />
                  </h3>

                  {/* First paragraph with image floated to the right */}
                  <div className="relative">
                    <img
                      src="/ai-safety-cat.png"
                      alt="AI Safety themed illustration with cat and holographic technology"
                      className="float-right ml-6 mb-4 w-64 md:w-80 rounded-xl shadow-lg hover:scale-105 transition-transform duration-300"
                    />
                    <p className="text-gray-700 leading-relaxed text-lg mb-4">
                      <strong>For everyone:</strong> I help make sure AI stays
                      helpful and safe for humanity. Think of it like being a
                      systems engineer for the smart computer systems that are
                      becoming part of our world – making sure they help us
                      rather than harm us.
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      <strong>For experts:</strong> I'm a DevOps engineer and
                      security automation specialist focused on building
                      reliable infrastructure for AI safety research. I design
                      secure, scalable systems that power machine learning
                      pipelines and experimentation frameworks for evaluating
                      frontier AI capabilities and potential risks.
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      My work spans automation, GitHub Actions, and
                      infrastructure as code using OpenTofu. I'm exploring
                      decentralized identity, verifiable compute, and trust
                      minimization in Web3 infrastructure. I collaborate with
                      research and engineering teams to deploy robust pipelines
                      for AI capability and safety evaluations.
                    </p>

                    {/* Clear float and add caption */}
                    <div className="clear-both"></div>
                    <p className="text-sm text-gray-500 italic text-center mb-4">
                      Future of AI safety research
                    </p>

                    <div className="bg-white/60 rounded-lg p-4">
                      <p className="text-gray-600 italic">
                        "I believe the future of AI infrastructure lies in
                        transparent, composable systems that keep humans in
                        control – always building for the users."
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tech Stack Visual */}
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="flex flex-col items-center p-4 bg-gradient-to-b from-blue-50 to-blue-100 rounded-xl hover:scale-105 transition-transform duration-300">
                    <Shield className="w-8 h-8 text-blue-600 mb-2" />
                    <span className="text-sm text-gray-700 font-medium text-center">
                      Security Automation
                    </span>
                  </div>
                  <div className="flex flex-col items-center p-4 bg-gradient-to-b from-purple-50 to-purple-100 rounded-xl hover:scale-105 transition-transform duration-300">
                    <Code className="w-8 h-8 text-purple-600 mb-2" />
                    <span className="text-sm text-gray-700 font-medium text-center">
                      Infrastructure as Code
                    </span>
                  </div>
                  <div className="flex flex-col items-center p-4 bg-gradient-to-b from-green-50 to-green-100 rounded-xl hover:scale-105 transition-transform duration-300">
                    <Zap className="w-8 h-8 text-green-600 mb-2" />
                    <span className="text-sm text-gray-700 font-medium text-center">
                      ML Pipelines
                    </span>
                  </div>
                  <div className="flex flex-col items-center p-4 bg-gradient-to-b from-orange-50 to-orange-100 rounded-xl hover:scale-105 transition-transform duration-300">
                    <Lock className="w-8 h-8 text-orange-600 mb-2" />
                    <span className="text-sm text-gray-700 font-medium text-center">
                      Web3 & Trust
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Transportation Advocacy */}
        <section className="transform hover:-translate-y-1 transition-transform duration-300">
          <Card className="border-none shadow-lg bg-white/80 backdrop-blur-sm">
            <CardContent className="p-8">
              <h2 className="text-2xl font-semibold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                Transportation Advocacy
              </h2>
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="flex flex-col items-center p-6 bg-gradient-to-b from-blue-50 to-blue-100 rounded-xl hover:scale-105 transition-transform duration-300">
                      <Train className="w-10 h-10 text-blue-600 mb-3" />
                      <span className="text-gray-700 font-medium text-center">
                        Rail Transit
                      </span>
                    </div>
                    <div className="flex flex-col items-center p-6 bg-gradient-to-b from-green-50 to-green-100 rounded-xl hover:scale-105 transition-transform duration-300">
                      <Bus className="w-10 h-10 text-green-600 mb-3" />
                      <span className="text-gray-700 font-medium text-center">
                        Bus Networks
                      </span>
                    </div>
                    <div className="flex flex-col items-center p-6 bg-gradient-to-b from-orange-50 to-orange-100 rounded-xl hover:scale-105 transition-transform duration-300">
                      <Bike className="w-10 h-10 text-orange-600 mb-3" />
                      <span className="text-gray-700 font-medium text-center">
                        Bike Infrastructure
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    For over a decade, I've dedicated myself to improving urban
                    transit, collaborating with agencies like the SFMTA to craft
                    policies that enhance rail, bus, bike, and multimodal
                    transportation. As the chair of Rescue Muni, I lead efforts
                    to advocate for enhanced rail service across San Francisco,
                    the Bay Area, and California.
                  </p>
                </div>
                <div>
                  <img
                    src="/sf-transit.png"
                    alt="San Francisco transit and transportation systems"
                    className="rounded-xl shadow-lg w-full hover:scale-105 transition-transform duration-300"
                  />
                  <p className="text-sm text-gray-500 mt-3 italic text-center">
                    San Francisco transportation advocacy
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Tech Accomplishments (placed above Let's Connect) */}
        <section className="transform hover:-translate-y-1 transition-transform duration-300">
          <Card className="border-none shadow-lg bg-white/80 backdrop-blur-sm">
            <CardContent className="p-8">
              <h2 className="text-2xl font-semibold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                Selected Technical Work
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                A short list of hackathons, open-source contributions, and recent
                public repositories.
              </p>
              <a
                href="/?page=accomplishments"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:scale-105 transition-transform duration-300 font-medium"
                onClick={() =>
                  trackLinkClick(
                    "Selected Technical Work",
                    "/?page=accomplishments"
                  )
                }
              >
                View accomplishments →
              </a>
            </CardContent>
          </Card>
        </section>

        {/* Contact Section */}
        <section className="transform hover:-translate-y-1 transition-transform duration-300">
          <Card className="border-none shadow-lg bg-white/80 backdrop-blur-sm">
            <CardContent className="p-8">
              <h2 className="text-2xl font-semibold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                Let's Connect
              </h2>
              <div className="flex flex-wrap gap-4 justify-center">
                <a
                  href="mailto:ballew@sublinear.net"
                  className="flex items-center space-x-3 px-6 py-3 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl hover:scale-105 transition-transform duration-300"
                  onClick={() =>
                    trackLinkClick("Email", "mailto:ballew@sublinear.net")
                  }
                >
                  <Mail className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700 font-medium">Email</span>
                </a>
                <a
                  href="https://www.linkedin.com/in/ballew/"
                  className="flex items-center space-x-3 px-6 py-3 bg-gradient-to-r from-blue-50 to-purple-100 rounded-xl hover:scale-105 transition-transform duration-300"
                  onClick={() =>
                    trackLinkClick(
                      "LinkedIn",
                      "https://www.linkedin.com/in/ballew/"
                    )
                  }
                >
                  <Linkedin className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700 font-medium">LinkedIn</span>
                </a>
                <a
                  href="https://twitter.com/ballew"
                  className="flex items-center space-x-3 px-6 py-3 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl hover:scale-105 transition-transform duration-300"
                  onClick={() =>
                    trackLinkClick("Twitter/X", "https://twitter.com/ballew")
                  }
                >
                  <span className="font-bold text-lg">𝕏</span>
                  <span className="text-gray-700 font-medium">X</span>
                </a>
                <a
                  href="https://github.com/markballew"
                  className="flex items-center space-x-3 px-6 py-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl hover:scale-105 transition-transform duration-300"
                  onClick={() =>
                    trackLinkClick("GitHub", "https://github.com/markballew")
                  }
                >
                  <Github className="w-5 h-5 text-gray-700" />
                  <span className="text-gray-700 font-medium">GitHub</span>
                </a>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>

      <footer className="text-center py-8 text-gray-600">
        <p>&copy; 2024 Mark Ballew</p>
      </footer>
    </div>
  );
};

export default LandingPage;
